import type {
  Calibration,
  CalibrationKind,
  CalibrationParams,
  Command,
  DeviceConfig,
  DeviceDataCounts,
  DeviceDetail,
  DeviceSummary,
  GatewaySummary,
  HistoryMetric,
  HistorySeries,
  IrrigationEvent,
  Schedule,
  ScheduleInput,
  Sensor,
} from '@irrigazione/shared';

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: init?.body ? { 'Content-Type': 'application/json' } : undefined,
  });
  if (!response.ok) {
    // Il server risponde sempre con { error } sui 4xx: mostrarlo tale quale
    // evita di inventare messaggi generici in UI.
    const detail = await response.json().catch(() => null);
    throw new Error(detail?.error ?? `Errore ${response.status}`);
  }
  return (await response.json()) as T;
}

export const api = {
  devices: () => request<DeviceSummary[]>('/api/devices'),
  device: (id: string) => request<DeviceDetail>(`/api/devices/${id}`),

  updateDevice: (id: string, patch: { name?: string; notes?: string | null; enabled?: boolean }) =>
    request<DeviceSummary>(`/api/devices/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),

  deleteDevice: (id: string) =>
    request<{ ok: true; removed: DeviceDataCounts }>(`/api/devices/${id}`, { method: 'DELETE' }),

  deleteDeviceData: (id: string) =>
    request<{ ok: true; removed: DeviceDataCounts }>(`/api/devices/${id}/data`, {
      method: 'DELETE',
    }),

  saveConfig: (id: string, config: DeviceConfig) =>
    request<DeviceConfig>(`/api/devices/${id}/config`, {
      method: 'PUT',
      body: JSON.stringify(config),
    }),

  createSchedule: (id: string, input: ScheduleInput) =>
    request<Schedule>(`/api/devices/${id}/schedules`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  updateSchedule: (id: string, scheduleId: number, input: ScheduleInput) =>
    request<Schedule>(`/api/devices/${id}/schedules/${scheduleId}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    }),

  deleteSchedule: (id: string, scheduleId: number) =>
    request<{ ok: true }>(`/api/devices/${id}/schedules/${scheduleId}`, { method: 'DELETE' }),

  saveCalibration: (id: string, sensor: Sensor, kind: CalibrationKind, params: CalibrationParams) =>
    request<Calibration>(`/api/devices/${id}/calibration/${sensor}`, {
      method: 'PUT',
      body: JSON.stringify({ kind, params }),
    }),

  recomputeHistory: (id: string) =>
    request<{ updated: number }>(`/api/devices/${id}/calibration/recompute`, { method: 'POST' }),

  irrigate: (id: string, minutes: number) =>
    request<{ command: Command; immediate: boolean; estimatedAt: string }>(
      `/api/devices/${id}/irrigate`,
      { method: 'POST', body: JSON.stringify({ minutes }) },
    ),

  stop: (id: string) =>
    request<{ command: Command }>(`/api/devices/${id}/stop`, { method: 'POST' }),

  history: (id: string, metric: HistoryMetric, from: Date, to: Date) => {
    const params = new URLSearchParams({
      metric,
      from: from.toISOString(),
      to: to.toISOString(),
    });
    return request<HistorySeries>(`/api/devices/${id}/history?${params}`);
  },

  events: (id: string, limit = 50) =>
    request<IrrigationEvent[]>(`/api/devices/${id}/events?limit=${limit}`),

  gateways: () => request<GatewaySummary[]>('/api/gateways'),
};
